import { PropsWithChildren, ReactElement, useEffect, useMemo } from 'react'
import { Option } from 'components/atoms/inputs/Select/parts/types'
import { Select } from 'components/atoms/inputs/Select/parts/Select'
import { Subject, merge } from 'rxjs'
import { takeUntil, filter, scan, tap, debounceTime } from 'rxjs/operators'

type TypeBufferHandlerProps<Value> = {
  context: Select<Value>
  options: Option<Value>[]
}
export const TypeBufferHandler = <Value extends any>(props: PropsWithChildren<TypeBufferHandlerProps<Value>>): ReactElement | null => {
  const {
    context,
    options,
    children,
  } = props

  const { inputRef, highlight } = context
  const { current: inputNode } = inputRef

  const inputSubject = useMemo(() => new Subject<KeyboardEvent>(), [])

  useEffect(() => {
    const resetSubject: Subject<null> = new Subject()
    const destroySubject = new Subject()

    merge(
      inputSubject.pipe(
        filter(({ keyCode }) => {
          return keyCode === 32 ||                    // space
            keyCode >= 48 && keyCode <= 90 ||         // 0 - z
            keyCode >= 186 && keyCode <= 192 ||       // ; - `
            keyCode >= 219 && keyCode <= 222          // [ - '
        }),
        takeUntil(destroySubject),
      ),
      resetSubject
    )
      .pipe(
        scan((acc, event) => {
          if (event === null) {
            return ''
          }

          return `${ acc }${ event.key }`.toLowerCase()
        }, ''),
        tap((buffer) => {
          if (buffer === '') {
            return
          }

          const index = options.findIndex((option) => option.label.toLowerCase().indexOf(buffer) !== -1)
          highlight.setIndex(index)
        }),
        debounceTime(1000),
        tap(() => resetSubject.next(null)),
      )
      .subscribe()

    return () => {
      destroySubject.next()
    }
  }, [options, highlight, inputSubject])

  useEffect(() => {
    if (!inputNode) {
      return
    }

    const handle = (event: KeyboardEvent) => {
      inputSubject.next(event)
    }

    inputNode.addEventListener('keydown', handle)
    return () => {
      inputNode.removeEventListener('keydown', handle)
    }
  }, [inputNode, inputSubject])

  return (
    <>
      { children }
    </>
  )
}
